import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'
import {
  ACTIVE_QUOTE_STATUSES,
  DELIVERED_STATUS,
} from 'src/shared/consts/quoteStatus'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { queryKeys } from './queryKeys'

export type CompanyBrainDoc_ =
  Database['public']['Tables']['company_brain_doc']['Row']
export type CompanyBrainSuggestion_ =
  Database['public']['Tables']['company_brain_suggestion']['Row']
export type CompanyMilestone_ =
  Database['public']['Tables']['company_milestone']['Row']
export type CompanyTodo_ = Database['public']['Tables']['company_todo']['Row']

export type CompanyMilestoneWithTodos = CompanyMilestone_ & {
  company_todo: CompanyTodo_[]
}

export type CompanyPulse = {
  activeRequests: number
  delivered: number
  organizations: number
  emailsWaiting: number
  pendingSuggestions: number
  openTodos: number
}

export const useCompanyBrainDocs = () => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [queryKeys.companyBrainDocs],
    queryFn: async (): Promise<CompanyBrainDoc_[]> => {
      const { data, error } = await supabase
        .from('company_brain_doc')
        .select('*')
        .order('slug')
      if (error) throw error
      return data ?? []
    },
    enabled: isAdmin,
  })
}

export const useCompanyPendingSuggestions = () => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [queryKeys.companyBrainSuggestions, 'pending'],
    queryFn: async (): Promise<CompanyBrainSuggestion_[]> => {
      const { data, error } = await supabase
        .from('company_brain_suggestion')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
    enabled: isAdmin,
  })
}

export const useCompanyPulse = () => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [queryKeys.companyPulse],
    queryFn: async (): Promise<CompanyPulse> => {
      const [
        active,
        delivered,
        orgs,
        emails,
        suggestions,
        todos,
      ] = await Promise.all([
        supabase
          .from('quote')
          .select('id', { count: 'exact', head: true })
          .in('status', [...ACTIVE_QUOTE_STATUSES]),
        supabase
          .from('quote')
          .select('id', { count: 'exact', head: true })
          .eq('status', DELIVERED_STATUS),
        supabase
          .from('organization')
          .select('id', { count: 'exact', head: true }),
        supabase
          .from('inbox_item')
          .select('id', { count: 'exact', head: true })
          .in('status', ['needs_draft', 'needs_review']),
        supabase
          .from('company_brain_suggestion')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('company_todo')
          .select('id', { count: 'exact', head: true })
          .eq('done', false),
      ])

      const firstError =
        active.error ||
        delivered.error ||
        orgs.error ||
        emails.error ||
        suggestions.error ||
        todos.error
      if (firstError) console.error('company pulse count failed', firstError)

      return {
        activeRequests: active.error ? 0 : (active.count ?? 0),
        delivered: delivered.error ? 0 : (delivered.count ?? 0),
        organizations: orgs.error ? 0 : (orgs.count ?? 0),
        emailsWaiting: emails.error ? 0 : (emails.count ?? 0),
        pendingSuggestions: suggestions.error ? 0 : (suggestions.count ?? 0),
        openTodos: todos.error ? 0 : (todos.count ?? 0),
      }
    },
    enabled: isAdmin,
  })
}

export const useCompanyMilestones = () => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [queryKeys.companyMilestones],
    queryFn: async (): Promise<CompanyMilestoneWithTodos[]> => {
      const { data, error } = await supabase
        .from('company_milestone')
        .select('*, company_todo (*)')
        .order('target_date', { ascending: true })
      if (error) throw error
      return (data ?? []).map((row) => ({
        ...row,
        company_todo: [...(row.company_todo ?? [])].sort(
          (a, b) => a.sort_order - b.sort_order
        ),
      }))
    },
    enabled: isAdmin,
  })
}

export const useToggleCompanyTodo = () => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await supabase
        .from('company_todo')
        .update({ done })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.companyMilestones] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.companyPulse] })
    },
  })
}

export const useAddCompanyTodo = () => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      milestoneId,
      title,
    }: {
      milestoneId: string
      title: string
    }) => {
      const { error } = await supabase.from('company_todo').insert({
        milestone_id: milestoneId,
        title,
        sort_order: 100,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.companyMilestones] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.companyPulse] })
    },
  })
}

export const useAddCompanyMilestone = () => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      title,
      targetDate,
    }: {
      title: string
      targetDate: string
    }) => {
      const { error } = await supabase.from('company_milestone').insert({
        title,
        summary: '',
        target_date: targetDate,
        status: 'upcoming',
        sort_order: 200,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.companyMilestones] })
    },
  })
}
